import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * Contas de demonstração do Restaurante Demo.
 *
 * 21/09 (decisão do dono): os acessos voltam a aparecer na tela de login, em
 * qualquer ambiente — é assim que o dono e os clientes conhecem o sistema.
 * O que NÃO volta é a conta do operador da plataforma (`admin@cardapio.com`),
 * que é a chave mestra de todos os restaurantes: ela continua fora da tela.
 *
 * O "Admin Demo" é SUPER_ADMIN **só do Restaurante Demo** — vê e mexe em tudo
 * do restaurante (cardápio, estoque, caixa, usuários), sem alcançar a
 * plataforma nem os outros clientes.
 */
const CONTAS_DEMO = [
  {
    emoji: '👨‍💼',
    papel: 'Admin do restaurante',
    descricao: 'vê e mexe em tudo do Restaurante Demo',
    email: 'admin-demo@cardapio.com',
    senha: 'Admin@123',
  },
  {
    emoji: '🧑‍🍳',
    papel: 'Garçom',
    descricao: 'anota pedidos e fecha contas',
    email: 'garcom-demo@cardapio.com',
    senha: 'Garcom@123',
  },
  {
    emoji: '👨‍🍳',
    papel: 'Cozinha',
    descricao: 'recebe e prepara os pedidos',
    email: 'cozinha-demo@cardapio.com',
    senha: 'Cozinha@123',
  },
];

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [escolhendoEmpresa, setEscolhendoEmpresa] = useState(false);
  const { login, selecionarEmpresa, empresas, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  if (isAuthenticated) {
    navigate('/restaurante');
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const novasEmpresas = await login(email, password);
      // Com 1 restaurante o login já seleciona; com vários, mostra a escolha.
      if (novasEmpresas.length > 1) {
        setEscolhendoEmpresa(true);
        setIsLoading(false);
      } else if (novasEmpresas.length === 0) {
        // Sem vínculo com restaurante: só o operador da plataforma tem o que fazer.
        const userSalvo = JSON.parse(localStorage.getItem('user') || 'null');
        if (userSalvo?.role === 'ADMIN') {
          navigate('/admin');
        } else {
          setError('Sua conta não está vinculada a nenhum restaurante. Peça ao administrador.');
          setIsLoading(false);
        }
      } else {
        navigate('/restaurante');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Erro ao fazer login');
      setIsLoading(false);
    }
  };

  const escolher = async (tenantId: string) => {
    setError('');
    setIsLoading(true);
    try {
      await selecionarEmpresa(tenantId);
      navigate('/restaurante');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao selecionar o restaurante');
      setIsLoading(false);
    }
  };

  /** Preenche email/senha com uma conta de teste — o usuário só clica em Entrar */
  const preencherTeste = (testEmail: string, testSenha: string) => {
    setEmail(testEmail);
    setPassword(testSenha);
    setError('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-950 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-100">
            PrimeClick
          </h2>
          <p className="mt-2 text-center text-sm text-gray-400">
            {escolhendoEmpresa
              ? 'Escolha o restaurante para acessar'
              : 'Acesse o painel administrativo'}
          </p>
        </div>

        {escolhendoEmpresa ? (
          <div className="mt-8 space-y-6">
            {error && (
              <div className="bg-red-950/60 border border-red-800 text-red-300 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}
            <div className="flex flex-col gap-3">
              {empresas.map((empresa) => (
                <button
                  key={empresa.id}
                  type="button"
                  onClick={() => escolher(empresa.id)}
                  disabled={isLoading}
                  className="btn-primary w-full"
                >
                  {empresa.nome}{' '}
                  <span className="opacity-70 text-sm">({empresa.perfil})</span>
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => {
                setEscolhendoEmpresa(false);
                setError('');
              }}
              className="btn-secondary w-full"
            >
              ← Voltar
            </button>
          </div>
        ) : (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-950/60 border border-red-800 text-red-300 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}
            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-field mt-1"
                  placeholder="voce@restaurante.com.br"
                />
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-300">
                  Senha
                </label>
                <input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-field mt-1"
                  placeholder="••••••••"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full"
            >
              {isLoading ? 'Entrando...' : 'Entrar'}
            </button>
          </form>
        )}

        {!escolhendoEmpresa && (
          <div className="pt-6 border-t border-gray-800">
            <p className="text-center text-sm font-semibold text-gray-200 mb-1">
              🎬 Restaurante Demo
            </p>
            <p className="text-center text-xs text-gray-500 mb-3">
              Contas de demonstração — clique numa conta para preencher o login
            </p>
            <div className="flex flex-col gap-2">
              {CONTAS_DEMO.map((conta) => (
                <button
                  key={conta.email}
                  type="button"
                  onClick={() => preencherTeste(conta.email, conta.senha)}
                  className="btn-secondary text-sm w-full text-left px-4 py-3"
                >
                  <span className="flex items-center justify-between w-full gap-2">
                    <span>
                      {conta.emoji} <span className="font-semibold">{conta.papel}</span>
                      <span className="text-gray-500"> · {conta.descricao}</span>
                    </span>
                  </span>
                  <span className="block font-mono text-[11px] text-gray-400 mt-1">
                    {conta.email} · {conta.senha}
                  </span>
                </button>
              ))}
            </div>
            <p className="text-center text-[11px] text-gray-600 mt-3">
              Seus clientes veem os mesmos botões? Não — essas contas são só do
              ambiente de demonstração.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}